import React, { useState, useEffect } from 'react';
import useSessionStorage from '../hooks/useSessionStorage';
import { Plus, Edit2, Trash2, Download, HeartHandshake } from 'lucide-react';
import Modal from '../components/Modal';
import { expensesService } from '../services/supabaseService';
import { isPersonalCategory, PERSONAL_CATEGORIES } from '../utils/expenseCategories';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell,
  PieChart, Pie
} from 'recharts';

const parseDateSafe = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;
  
  const datePart = typeof dateStr === 'string' ? dateStr.split('T')[0].split(' ')[0] : '';
  const parts = datePart.split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }
  return new Date(dateStr);
};

const formatDateLocal = (isoStr) => {
  if (!isoStr) return '';
  const d = parseDateSafe(isoStr);
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const PersonalExpenses = () => {
  const [allExpenses, setAllExpenses] = useState([]);
  const [isModalOpen, setIsModalOpen] = useSessionStorage('personalExpenses_isModalOpen', false);
  const [editingExpense, setEditingExpense] = useSessionStorage('personalExpenses_editingExpense', null);
  const [filterCategory, setFilterCategory] = useSessionStorage('personalExpenses_filterCategory', 'All');
  const [filterSubcategory, setFilterSubcategory] = useSessionStorage('personalExpenses_filterSubcategory', 'All');
  const [startDate, setStartDate] = useSessionStorage('personalExpenses_startDate', '');
  const [endDate, setEndDate] = useSessionStorage('personalExpenses_endDate', '');
  const [filterMonth, setFilterMonth] = useSessionStorage('personalExpenses_filterMonth', '');
  
  // Dynamic Category Map
  const [categoryMap, setCategoryMap] = useState({});

  // Form State
  const [formData, setFormData] = useSessionStorage('personalExpenses_formData', {
    description: '', amount: '', category: 'Personal', subcategory: 'General', quantity: 1, metric: 'units', expense_date: ''
  });

  // Custom Category/Subcategory States
  const [isAddingSubcategory, setIsAddingSubcategory] = useState(false);
  const [newSubcategoryName, setNewSubcategoryName] = useState('');
  const [deleteConfirmExpenseId, setDeleteConfirmExpenseId] = useState(null);

  const fetchExpenses = async () => {
    try {
      const data = await expensesService.getAll();
      setAllExpenses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching expenses:', err);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await expensesService.getCategories();
      if (data && Object.keys(data).length > 0) {
        setCategoryMap(data);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  useEffect(() => {
    Promise.all([
      fetchExpenses(),
      fetchCategories()
    ]);
  }, []);

  // Filter only personal expenses
  const personalExpenses = allExpenses.filter(e => isPersonalCategory(e.category));

  const handleOpenModal = (expense = null) => {
    setIsAddingSubcategory(false);
    setNewSubcategoryName('');

    if (expense) {
      setEditingExpense(expense);
      
      let currentMap = { ...categoryMap };
      let mapChanged = false;
      
      if (!currentMap[expense.category]) {
        currentMap[expense.category] = [];
        mapChanged = true;
      }
      if (expense.subcategory && !currentMap[expense.category].includes(expense.subcategory)) {
        currentMap[expense.category].push(expense.subcategory);
        mapChanged = true;
      }
      if (mapChanged) setCategoryMap(currentMap);

      setFormData({
        description: expense.description,
        amount: expense.amount,
        category: expense.category || 'Personal',
        subcategory: expense.subcategory || currentMap[expense.category || 'Personal']?.[0] || 'General',
        quantity: expense.quantity || 1,
        metric: expense.metric || 'units',
        expense_date: formatDateLocal(expense.expense_date)
      });
    } else {
      setEditingExpense(null);
      const d = new Date();
      const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const defaultCat = 'Personal';
      const defaultSubcat = categoryMap[defaultCat]?.[0] || 'General';
      setFormData({ description: '', amount: '', category: defaultCat, subcategory: defaultSubcat, quantity: 1, metric: 'units', expense_date: today });
    }
    setIsModalOpen(true);
  };

  const handleCategoryChange = (e) => {
    const val = e.target.value;
    setIsAddingSubcategory(false);
    setFormData({
      ...formData,
      category: val,
      subcategory: categoryMap[val]?.[0] || 'General'
    });
  };

  const handleSubcategoryChange = (e) => {
    const val = e.target.value;
    if (val === '__ADD_NEW__') {
      setIsAddingSubcategory(true);
      setFormData({ ...formData, subcategory: '' });
    } else {
      setIsAddingSubcategory(false);
      setFormData({ ...formData, subcategory: val });
    }
  };

  const confirmNewSubcategory = async () => {
    const subcatName = newSubcategoryName.trim();
    if (subcatName) {
      const updatedMap = { ...categoryMap };
      const currentList = updatedMap[formData.category] || [];
      if (!currentList.includes(subcatName)) {
        updatedMap[formData.category] = [...currentList, subcatName];
      }
      setCategoryMap(updatedMap);
      setFormData({ ...formData, subcategory: subcatName });
      setIsAddingSubcategory(false);
      setNewSubcategoryName('');

      try {
        await expensesService.saveCategory(formData.category, updatedMap[formData.category]);
      } catch (err) {
        console.error('Error saving subcategory:', err);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingExpense) {
        await expensesService.update(editingExpense.id, formData);
      } else {
        await expensesService.create(formData);
      }
      setIsModalOpen(false);
      fetchExpenses();
    } catch (err) {
      console.error('Error saving personal expense:', err);
      alert('Error saving personal expense: ' + err.message);
    }
  };

  const handleDelete = (id) => {
    setDeleteConfirmExpenseId(id);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmExpenseId) return;
    try {
      await expensesService.delete(deleteConfirmExpenseId);
      setDeleteConfirmExpenseId(null);
      fetchExpenses();
    } catch (err) {
      console.error('Error deleting personal expense:', err);
      alert('Error deleting personal expense: ' + err.message);
    }
  };

  // Filtered personal expenses
  const filteredExpenses = personalExpenses.filter(e => {
    const passCategory = filterCategory === 'All' ? true : e.category === filterCategory;
    const passSubcategory = filterSubcategory === 'All' ? true : (e.subcategory || '').toLowerCase() === filterSubcategory.toLowerCase();
    
    let passStartDate = true;
    let passEndDate = true;
    let passFilterMonth = true;
    
    const expDateStr = formatDateLocal(e.expense_date);
    
    if (startDate && expDateStr) {
      passStartDate = expDateStr >= startDate;
    }
    if (endDate && expDateStr) {
      passEndDate = expDateStr <= endDate;
    }
    if (filterMonth && expDateStr) {
      passFilterMonth = expDateStr.substring(0, 7) === filterMonth;
    }
    
    return passCategory && passSubcategory && passStartDate && passEndDate && passFilterMonth;
  });

  const [currentPage, setCurrentPage] = useSessionStorage('personalExpenses_currentPage', 1);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [filterCategory, filterSubcategory, startDate, endDate, filterMonth]);

  const totalPages = Math.ceil(filteredExpenses.length / itemsPerPage) || 1;
  const paginatedExpenses = filteredExpenses.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const availableSubcategories = filterCategory === 'All' ? [] : (categoryMap[filterCategory] || []);

  const totalThisMonth = personalExpenses.reduce((acc, curr) => {
    const d = parseDateSafe(curr.expense_date);
    if (!d) return acc;
    const now = new Date();
    if (d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()) {
      return acc + Number(curr.amount || 0);
    }
    return acc;
  }, 0);

  const totalLastMonth = personalExpenses.reduce((acc, curr) => {
    const d = parseDateSafe(curr.expense_date);
    if (!d) return acc;
    const now = new Date();
    let lastMonth = now.getMonth() - 1;
    let year = now.getFullYear();
    if (lastMonth < 0) {
      lastMonth = 11;
      year -= 1;
    }
    if (d.getMonth() === lastMonth && d.getFullYear() === year) {
      return acc + Number(curr.amount || 0);
    }
    return acc;
  }, 0);

  const totalFilteredAmount = filteredExpenses.reduce((acc, curr) => acc + Number(curr.amount || 0), 0);

  const thisMonthName = new Date().toLocaleString('default', { month: 'long' });
  const lastMonthDate = new Date();
  lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
  const lastMonthName = lastMonthDate.toLocaleString('default', { month: 'long' });

  const categoryData = Object.entries(
    filteredExpenses.reduce((acc, exp) => {
      const cat = exp.category ? exp.category.trim() : 'Personal';
      acc[cat] = (acc[cat] || 0) + Number(exp.amount || 0);
      return acc;
    }, {})
  ).map(([name, amount]) => ({ name, amount }));

  const pieData = (() => {
    const sorted = [...categoryData].sort((a, b) => b.amount - a.amount);
    if (sorted.length <= 6) return sorted;
    const top5 = sorted.slice(0, 5);
    const otherSum = sorted.slice(5).reduce((sum, item) => sum + item.amount, 0);
    return [...top5, { name: 'Other', amount: otherSum }];
  })();

  const COLORS = [
    '#ec4899', // pink
    '#8b5cf6', // purple
    '#3b82f6', // blue
    '#f59e0b', // amber
    '#10b981', // green
    '#ef4444'  // red
  ];

  const handleExport = () => {
    const headers = ['Date', 'Description', 'Category', 'Subcategory', 'Quantity', 'Metric Unit', 'Amount (₹)'];
    const rows = [headers];
    filteredExpenses.forEach(exp => {
      rows.push([
        formatDateLocal(exp.expense_date),
        `"${(exp.description || '').replace(/"/g, '""')}"`,
        `"${exp.category || ''}"`,
        `"${exp.subcategory || ''}"`,
        exp.quantity || 1,
        exp.metric || 'units',
        exp.amount || 0
      ]);
    });

    const csvContent = "data:text/csv;charset=utf-8," + rows.map(e => e.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Personal_Expenses_Export_${filterMonth || 'All'}.csv`);
    document.body.appendChild(link);
    link.click();
  };

  return (
    <div className="expenses-page">
      <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
        <div>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <HeartHandshake size={26} color="#ec4899" />
            Personal Expenses
          </h2>
          <p style={{ color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
            Track personal withdrawals, medical, family, and religious expenditures excluded from PG net profit.
          </p>
        </div>
        <div className="flex gap-4">
          <button className="btn-secondary flex items-center gap-2" onClick={handleExport}>
            <Download size={16} /> Export CSV
          </button>
          <button className="btn-primary flex items-center gap-2" onClick={() => handleOpenModal()}>
            <Plus size={16} /> Record Personal Expense
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6 mb-8">
        <div className="card">
          <div className="card-title">This Month ({thisMonthName})</div>
          <div className="card-value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#ec4899' }}>
            ₹{totalThisMonth.toLocaleString()}
          </div>
        </div>
        <div className="card">
          <div className="card-title">Last Month ({lastMonthName})</div>
          <div className="card-value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-primary)' }}>
            ₹{totalLastMonth.toLocaleString()}
          </div>
        </div>
        <div className="card">
          <div className="card-title">Total {filterCategory === 'All' ? 'Personal Amount' : `${filterCategory}${filterSubcategory !== 'All' ? ` - ${filterSubcategory}` : ''}`}</div>
          <div className="card-value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-primary)' }}>
            ₹{totalFilteredAmount.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Category-wise Personal Expenses Chart */}
      <div className="card mb-8" style={{ padding: '1.5rem' }}>
        <h3 style={{ fontSize: '1rem', marginBottom: '1.5rem', marginTop: 0, color: 'var(--text-primary)', fontWeight: '600' }}>
          Personal Expense Breakdown Analysis
        </h3>
        {categoryData.length === 0 ? (
          <div style={{ height: '280px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
            No personal expense data available to display charts.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '2.5rem' }}>
            {/* Bar Chart */}
            <div>
              <h4 style={{ margin: '0 0 1rem 0', color: 'var(--text-secondary)', fontSize: '0.875rem', fontWeight: '500' }}>Category-wise Amounts</h4>
              <div style={{ height: '280px', width: '100%', minWidth: 0 }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                  <BarChart data={categoryData} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
                    <XAxis 
                      dataKey="name" 
                      stroke="var(--text-secondary)" 
                      tick={{ fontSize: 11 }} 
                    />
                    <YAxis 
                      stroke="var(--text-secondary)" 
                      tick={{ fontSize: 11 }} 
                      tickFormatter={(val) => `₹${val}`}
                    />
                    <RechartsTooltip 
                      contentStyle={{ backgroundColor: 'var(--surface-color)', borderColor: 'var(--border-color)', borderRadius: '0.5rem', color: '#fff', fontSize: '0.85rem' }}
                      itemStyle={{ color: '#fff' }}
                      formatter={(value) => [`₹${Number(value).toLocaleString()}`, 'Total Spent']}
                    />
                    <Bar dataKey="amount" fill="#ec4899" radius={[4, 4, 0, 0]} maxBarSize={50}>
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Pie Chart */}
            <div>
              <h4 style={{ margin: '0 0 1rem 0', color: 'var(--text-secondary)', fontSize: '0.875rem', fontWeight: '500' }}>Distribution Share</h4>
              <div style={{ height: '280px', width: '100%', minWidth: 0 }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={80}
                      dataKey="amount"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      contentStyle={{ backgroundColor: 'var(--surface-color)', borderColor: 'var(--border-color)', borderRadius: '0.5rem', color: '#fff', fontSize: '0.85rem' }}
                      itemStyle={{ color: '#fff' }}
                      formatter={(value) => [`₹${Number(value).toLocaleString()}`, 'Amount']}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          <h3 style={{ margin: 0 }}>Personal Records ({filteredExpenses.length})</h3>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>From:</span>
              <input 
                type="date" 
                value={startDate} 
                onChange={e => setStartDate(e.target.value)}
                style={{ padding: '0.5rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)', fontSize: '0.875rem' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>To:</span>
              <input 
                type="date" 
                value={endDate} 
                onChange={e => setEndDate(e.target.value)}
                style={{ padding: '0.5rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)', fontSize: '0.875rem' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Month:</span>
              <input 
                type="month" 
                value={filterMonth || ''} 
                onChange={e => setFilterMonth(e.target.value)}
                style={{ 
                  padding: '0.5rem', 
                  borderRadius: '0.5rem', 
                  border: '1px solid var(--border-color)', 
                  backgroundColor: 'rgba(0,0,0,0.2)', 
                  color: 'var(--text-primary)', 
                  fontSize: '0.875rem',
                  colorScheme: 'dark'
                }}
              />
            </div>
            <select 
              value={filterCategory}
              onChange={(e) => { setFilterCategory(e.target.value); setFilterSubcategory('All'); }}
              style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
            >
              <option value="All" style={{ color: '#000' }}>All Personal Categories</option>
              {PERSONAL_CATEGORIES.map(cat => (
                <option key={cat} value={cat} style={{ color: '#000' }}>{cat}</option>
              ))}
            </select>

            {filterCategory !== 'All' && availableSubcategories.length > 0 && (
              <select 
                value={filterSubcategory}
                onChange={(e) => setFilterSubcategory(e.target.value)}
                style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
              >
                <option value="All" style={{ color: '#000' }}>All Subcategories</option>
                {availableSubcategories.map(sub => (
                  <option key={sub} value={sub} style={{ color: '#000' }}>{sub}</option>
                ))}
              </select>
            )}
          </div>
        </div>
        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                <th style={{ padding: '1rem 0', fontWeight: '500', minWidth: '130px' }}>Date</th>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Description</th>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Category</th>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Subcategory</th>
                <th style={{ padding: '1rem 0', fontWeight: '500', textAlign: 'center' }}>Quantity</th>
                <th style={{ padding: '1rem 0', fontWeight: '500', textAlign: 'center' }}>Unit</th>
                <th style={{ padding: '1rem 0', fontWeight: '500', textAlign: 'right' }}>Amount</th>
                <th style={{ padding: '1rem 0', fontWeight: '500', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedExpenses.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    No personal expense records found.
                  </td>
                </tr>
              ) : (
                paginatedExpenses.map((expense) => {
                  return (
                    <tr key={expense.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '1rem 0', color: 'var(--text-secondary)' }}>
                        {formatDateLocal(expense.expense_date)}
                      </td>
                      <td style={{ padding: '1rem 0', fontWeight: '500' }}>{expense.description}</td>
                      <td style={{ padding: '1rem 0' }}>
                        <span className="badge" style={{ backgroundColor: 'rgba(236, 72, 153, 0.15)', color: '#ec4899' }}>
                          {expense.category}
                        </span>
                      </td>
                      <td style={{ padding: '1rem 0' }}>
                        {expense.subcategory ? (
                          <span className="badge" style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: 'var(--text-primary)' }}>
                            {expense.subcategory}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>-</span>
                        )}
                      </td>
                      <td style={{ padding: '1rem 0', textAlign: 'center' }}>{expense.quantity || 1}</td>
                      <td style={{ padding: '1rem 0', textAlign: 'center', color: 'var(--text-secondary)' }}>{expense.metric || 'units'}</td>
                      <td style={{ padding: '1rem 0', textAlign: 'right', fontWeight: 'bold', color: '#ec4899' }}>
                        ₹{Number(expense.amount).toLocaleString()}
                      </td>
                      <td style={{ padding: '1rem 0', textAlign: 'right' }}>
                        <div className="flex justify-end gap-2">
                          <button 
                            className="btn-icon" 
                            title="Edit"
                            onClick={() => handleOpenModal(expense)}
                          >
                            <Edit2 size={16} />
                          </button>
                          <button 
                            className="btn-icon text-danger" 
                            title="Delete"
                            onClick={() => handleDelete(expense.id)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredExpenses.length)} of {filteredExpenses.length} entries
            </span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                className="btn-secondary" 
                style={{ padding: '0.4rem 0.8rem', fontSize: '0.875rem' }}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              >
                Previous
              </button>
              {[...Array(totalPages)].map((_, idx) => {
                const p = idx + 1;
                if (p === 1 || p === totalPages || (p >= currentPage - 1 && p <= currentPage + 1)) {
                  return (
                    <button
                      key={p}
                      className={currentPage === p ? "btn-primary" : "btn-secondary"}
                      style={{ padding: '0.4rem 0.8rem', fontSize: '0.875rem', minWidth: '32px' }}
                      onClick={() => setCurrentPage(p)}
                    >
                      {p}
                    </button>
                  );
                } else if (p === currentPage - 2 || p === currentPage + 2) {
                  return <span key={p} style={{ padding: '0.4rem', color: 'var(--text-secondary)' }}>...</span>;
                }
                return null;
              })}
              <button 
                className="btn-secondary" 
                style={{ padding: '0.4rem 0.8rem', fontSize: '0.875rem' }}
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        title={editingExpense ? "Edit Personal Expense" : "Record Personal Expense"}
      >
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Expense Description
            </label>
            <input 
              type="text" 
              required
              className="input-field" 
              placeholder="e.g., Grocery medicine, temple flower, pooja oil..."
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                Category
              </label>
              <select 
                className="input-field"
                value={formData.category}
                onChange={handleCategoryChange}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
              >
                {PERSONAL_CATEGORIES.map(cat => (
                  <option key={cat} value={cat} style={{ color: '#000' }}>{cat}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                Subcategory
              </label>
              {!isAddingSubcategory ? (
                <select 
                  className="input-field"
                  value={formData.subcategory}
                  onChange={handleSubcategoryChange}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
                >
                  {(categoryMap[formData.category] || ['General']).map(sub => (
                    <option key={sub} value={sub} style={{ color: '#000' }}>{sub}</option>
                  ))}
                  <option value="__ADD_NEW__" style={{ color: 'var(--primary-color)', fontWeight: 'bold' }}>+ Add Subcategory...</option>
                </select>
              ) : (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input 
                    type="text" 
                    placeholder="New subcategory" 
                    value={newSubcategoryName}
                    onChange={e => setNewSubcategoryName(e.target.value)}
                    style={{ flex: 1, padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
                  />
                  <button type="button" className="btn-primary" style={{ padding: '0.5rem 0.75rem' }} onClick={confirmNewSubcategory}>Add</button>
                  <button type="button" className="btn-secondary" style={{ padding: '0.5rem 0.75rem' }} onClick={() => setIsAddingSubcategory(false)}>✕</button>
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                Quantity
              </label>
              <input 
                type="number" 
                step="any"
                min="0"
                className="input-field" 
                value={formData.quantity}
                onChange={e => setFormData({ ...formData, quantity: e.target.value })}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                Metric / Unit
              </label>
              <input 
                type="text" 
                className="input-field" 
                placeholder="units, kg, items..."
                value={formData.metric}
                onChange={e => setFormData({ ...formData, metric: e.target.value })}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                Amount (₹)
              </label>
              <input 
                type="number" 
                step="any"
                required
                className="input-field" 
                placeholder="0.00"
                value={formData.amount}
                onChange={e => setFormData({ ...formData, amount: e.target.value })}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                Date
              </label>
              <input 
                type="date" 
                required
                className="input-field" 
                value={formData.expense_date}
                onChange={e => setFormData({ ...formData, expense_date: e.target.value })}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button type="button" className="btn-secondary" onClick={() => setIsModalOpen(false)}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {editingExpense ? 'Update Expense' : 'Save Personal Expense'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={Boolean(deleteConfirmExpenseId)}
        onClose={() => setDeleteConfirmExpenseId(null)}
        title="Confirm Delete"
      >
        <div style={{ padding: '1rem 0' }}>
          <p style={{ color: 'var(--text-primary)', marginBottom: '1.5rem' }}>
            Are you sure you want to delete this personal expense record? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={() => setDeleteConfirmExpenseId(null)}
            >
              Cancel
            </button>
            <button 
              type="button" 
              className="btn-danger"
              style={{ backgroundColor: 'var(--danger-color)', color: '#fff', padding: '0.5rem 1rem', borderRadius: '0.5rem' }}
              onClick={confirmDelete}
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PersonalExpenses;
